import React, { useState } from 'react';
import { Button, Alert, Modal, Spinner, RowActions, StatusBadge, DataTable, type DataTableColumn } from '@/components/ui';
import { format } from 'date-fns';
import { VendorInvoice, VendorInvoiceAttachment } from '../../../types/VendorInvoice';
import { formatDollars } from '../../../utils/formatDollars';
import { parseCalendarDate } from '../../../utils/dateUtils';
import { useClientSort } from '../../../utils/useClientSort';

const SORT_VALUES: Record<string, (inv: VendorInvoice) => string | number | null | undefined> = {
  invoiceDate: (inv) => (inv.invoiceDate ? new Date(inv.invoiceDate).getTime() : null),
  invoiceValue: (inv) => inv.invoiceValue,
  invoiceNumber: (inv) => inv.invoiceNumber,
  status: (inv) => inv.status,
};

interface VendorInvoiceListProps {
  invoices: VendorInvoice[];
  onEdit: (invoice: VendorInvoice) => void;
  onDelete: (invoiceId: number) => Promise<void>;
  onViewAttachment: (attachment: VendorInvoiceAttachment, invoice: VendorInvoice) => void;
  onRetryUpload: (invoiceId: number, attachmentId: number) => Promise<void>;
  loading?: boolean;
}

const VendorInvoiceList: React.FC<VendorInvoiceListProps> = ({
  invoices,
  onEdit,
  onDelete,
  onViewAttachment,
  onRetryUpload,
  loading = false,
}) => {
  const [deletingInvoiceId, setDeletingInvoiceId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<VendorInvoice | null>(null);
  const { sorted, sortBy, sortDir, onSort } = useClientSort(invoices, SORT_VALUES);

  const handleDeleteClick = (invoice: VendorInvoice) => {
    setInvoiceToDelete(invoice);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!invoiceToDelete) return;
    setDeletingInvoiceId(invoiceToDelete.id);
    try {
      await onDelete(invoiceToDelete.id);
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
    } finally {
      setDeletingInvoiceId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setInvoiceToDelete(null);
  };

  const formatDate = (date: string | Date) => {
    try {
      // invoiceDate is a calendar date (no meaningful time-of-day), but the API returns it as
      // a UTC-midnight ISO string. Parsing that with `new Date(str)` and formatting with
      // date-fns (which reads local getters) shows the *previous* day in any timezone behind
      // UTC — parseCalendarDate anchors it at local midnight for the same calendar day instead.
      const dateObj = parseCalendarDate(date);
      if (!dateObj) return 'Invalid date';
      return format(dateObj, 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const handleViewAttachment = (attachment: VendorInvoiceAttachment, invoice: VendorInvoice) => {
    const isPlaceholder = attachment.attachmentURL?.startsWith?.('placeholder://');
    if (isPlaceholder) {
      alert('File upload failed. This attachment could not be uploaded to storage. Please try uploading the file again.');
      return;
    }
    onViewAttachment(attachment, invoice);
  };

  if (loading) {
    return (
      <div className="tw-text-center tw-p-4">
        <Spinner>Loading...</Spinner>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <Alert variant="info" className="tw-text-center">
        No vendor invoices found for this project.
      </Alert>
    );
  }

  const columns: DataTableColumn<VendorInvoice>[] = [
    {
      key: 'invoiceNumber',
      header: 'Invoice #',
      sortable: true,
      mobile: 'title',
      cell: (invoice) => invoice.invoiceNumber || '-',
    },
    {
      key: 'invoiceDate',
      header: 'Date',
      sortable: true,
      mobile: 'subtitle',
      cell: (invoice) => formatDate(invoice.invoiceDate),
    },
    {
      key: 'invoiceValue',
      header: 'Value',
      sortable: true,
      className: 'tw-font-bold',
      cell: (invoice) => formatDollars(invoice.invoiceValue),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      mobile: 'trailing',
      // 'New' isn't in the shared STATUS_VARIANTS map (utils/statusUtils.ts) — falls
      // back to a neutral grey badge until it's added centrally.
      cell: (invoice) => <StatusBadge status={invoice.status} vocabulary="invoice" />,
    },
    {
      key: 'attachments',
      header: 'Attachments',
      cell: (invoice) =>
        invoice.attachments && invoice.attachments.length > 0 ? (
          <div className="tw-flex tw-flex-wrap tw-gap-1">
            {invoice.attachments.map((attachment) => {
              const isPlaceholder = attachment.attachmentURL?.startsWith?.('placeholder://');
              return (
                <div key={attachment.id} className="tw-flex tw-gap-1">
                  <Button
                    size="sm"
                    variant={isPlaceholder ? 'outline-warning' : 'outline-primary'}
                    onClick={() => handleViewAttachment(attachment, invoice)}
                    title={
                      isPlaceholder
                        ? `Upload failed: ${attachment.attachmentName}`
                        : attachment.attachmentNotes || attachment.attachmentName
                    }
                  >
                    <i className={`bi ${isPlaceholder ? 'bi-exclamation-triangle' : 'bi-file-pdf'} tw-mr-1`} />
                    {isPlaceholder ? 'Failed' : 'PDF'}
                  </Button>
                  {isPlaceholder && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => onRetryUpload(invoice.id, attachment.id)}
                      title="Retry upload"
                    >
                      <i className="bi bi-arrow-clockwise" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <span className="tw-text-content-muted">No attachments</span>
        ),
    },
    {
      key: 'comments',
      header: 'Comments',
      cell: (invoice) =>
        invoice.comments ? (
          <span title={invoice.comments} className="tw-truncate tw-inline-block" style={{ maxWidth: '150px' }}>
            {invoice.comments}
          </span>
        ) : (
          <span className="tw-text-content-muted">-</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'trailing',
      cell: (invoice) => (
        <RowActions
          actions={[
            { kind: 'edit', label: 'Edit invoice', onClick: () => onEdit(invoice) },
            {
              kind: 'delete',
              label: 'Delete invoice',
              onClick: () => handleDeleteClick(invoice),
              disabled: deletingInvoiceId === invoice.id,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="tw-w-full">
      <DataTable columns={columns} data={sorted} rowKey={(invoice) => invoice.id} striped hover sortBy={sortBy} sortDir={sortDir} onSort={onSort} />

      <Modal show={showDeleteModal} onHide={handleDeleteCancel}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete vendor invoice #{invoiceToDelete?.invoiceNumber}? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={deletingInvoiceId === invoiceToDelete?.id}
          >
            {deletingInvoiceId === invoiceToDelete?.id ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VendorInvoiceList;

